import React, { useEffect, useState, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import {
    Typography,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Tooltip,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core';
import {
    Search as SearchIcon,
    Clear as ClearIcon,
} from '@material-ui/icons';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { EMPTY_ROW_MSG, ERROR_MSG } from '../../../shared/lib/Localization';
import { getCatalogStockOrderTasks, getCatalogStockOrderTaskItems } from '../../api';
import { CatalogStockOrderTask, CatalogStockOrderTaskItem, CatalogStockOrderTaskStatus, CatalogStockOrderTaskItemStatus } from '../../models';
import { ErrorStatusBar } from '../../../shared/components';
import { blue, green, red, orange, grey } from '@material-ui/core/colors';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(2),
        },
        pageTitle: {
            marginBottom: theme.spacing(2),
        },
        taskItemsContainer: {
            padding: theme.spacing(2),
            marginTop: theme.spacing(1),
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
        },
        taskItemsTitle: {
            marginBottom: theme.spacing(1),
            fontWeight: 600,
        },
        filterRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        errorText: {
            color: red[700],
            fontSize: '0.8rem',
            maxWidth: 300,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
        },
        summaryChips: {
            display: 'flex',
            gap: theme.spacing(0.5),
            flexWrap: 'wrap',
        },
        progressMessageBox: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
            backgroundColor: theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        progressMessageLabel: {
            fontWeight: 600,
            marginBottom: theme.spacing(0.5),
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
        },
    })
);

const STATUS_COLORS: Record<string, string> = {
    [CatalogStockOrderTaskStatus.SUCCESS]: green[600],
    [CatalogStockOrderTaskStatus.FAILURE]: red[600],
    [CatalogStockOrderTaskStatus.PENDING]: grey[500],
    [CatalogStockOrderTaskStatus.STARTED]: blue[600],
    [CatalogStockOrderTaskStatus.PROGRESS]: blue[400],
    [CatalogStockOrderTaskStatus.PARTIAL_SUCCESS]: orange[600],
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const color = STATUS_COLORS[status] || grey[500];
    return (
        <Chip
            label={status}
            size="small"
            style={{
                backgroundColor: color,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.72rem',
            }}
        />
    );
};

const TASK_ITEM_STATUS_OPTIONS = [
    { value: '', label: 'All' },
    { value: CatalogStockOrderTaskItemStatus.PENDING, label: 'Pending' },
    { value: CatalogStockOrderTaskItemStatus.SUCCESS, label: 'Success' },
    { value: CatalogStockOrderTaskItemStatus.FAILURE, label: 'Failure' },
];

interface TaskItemsTableProps {
    taskId: number;
    classes: ReturnType<typeof useStyles>;
}

// Universal Catalog Ordering (FR-02): per-item resolution within a task -
// whether the line matched an existing Product or required a temporary one
// to be created, plus any Korona posting error.
const TaskItemsTable: React.FC<TaskItemsTableProps> = ({ taskId, classes }) => {
    const [items, setItems] = useState<CatalogStockOrderTaskItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState('');
    const [orderBy, setOrderBy] = useState('');

    const fetchItems = useCallback(() => {
        setLoading(true);
        setError(null);
        getCatalogStockOrderTaskItems(taskId, page + 1, pageSize, orderBy || undefined, statusFilter || undefined)
            .then(checkStatus)
            .then(parseJSON)
            .then((data: any) => {
                setItems(data.results || data);
                setTotalCount(data.count || (data.results || data).length);
                setLoading(false);
            })
            .catch((err: any) => {
                handleError(err);
                setError(ERROR_MSG);
                setLoading(false);
            });
    }, [taskId, page, pageSize, orderBy, statusFilter]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const columns = [
        {
            title: 'Master Product',
            field: 'master_product_name',
            width: 220,
        },
        {
            title: 'Reorder Code',
            field: 'reorder_code',
            width: 140,
        },
        {
            title: 'Case Qty',
            field: 'case_qty',
            width: 100,
            type: 'numeric' as const,
        },
        {
            title: 'Quantity',
            field: 'quantity',
            width: 100,
            type: 'numeric' as const,
        },
        {
            title: 'Resolution',
            field: 'resolution',
            width: 160,
            render: (row: CatalogStockOrderTaskItem) =>
                row.resolution === 'TEMP_CREATED' ? (
                    <Chip label="Temp Created" size="small" style={{ backgroundColor: orange[100], color: orange[800] }} />
                ) : row.resolution === 'MATCHED_EXISTING' ? (
                    <Chip label="Matched Existing" size="small" style={{ backgroundColor: green[100], color: green[800] }} />
                ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                ),
        },
        {
            title: 'Product',
            field: 'product_name',
            render: (row: CatalogStockOrderTaskItem) =>
                row.product_name ? (
                    <Typography variant="body2">{row.product_number ? `${row.product_number} - ${row.product_name}` : row.product_name}</Typography>
                ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                ),
        },
        {
            title: 'Status',
            field: 'status',
            width: 120,
            render: (row: CatalogStockOrderTaskItem) => <StatusChip status={row.status} />,
        },
        {
            title: 'Error Message',
            field: 'error_message',
            render: (row: CatalogStockOrderTaskItem) =>
                row.error_message ? (
                    <Tooltip title={row.error_message} placement="top-start">
                        <Typography className={classes.errorText}>
                            {row.error_message.length > 80 ? row.error_message.substring(0, 80) + '…' : row.error_message}
                        </Typography>
                    </Tooltip>
                ) : (
                    <Typography variant="body2" color="textSecondary">—</Typography>
                ),
        },
    ];

    return (
        <div className={classes.taskItemsContainer}>
            <Typography variant="subtitle2" className={classes.taskItemsTitle}>
                Task Items
            </Typography>
            <div className={classes.filterRow}>
                <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                    <InputLabel id="catalog-task-item-status-filter-label">Filter by Status</InputLabel>
                    <Select
                        labelId="catalog-task-item-status-filter-label"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value as string); setPage(0); }}
                        label="Filter by Status"
                    >
                        {TASK_ITEM_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value || '_all'} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {error && <ErrorStatusBar message={error} />}
            <MaterialTable
                columns={columns}
                data={items}
                isLoading={loading}
                options={{
                    toolbar: false,
                    paging: true,
                    pageSize,
                    pageSizeOptions: [10, 20, 50],
                    search: false,
                    sorting: true,
                    draggable: false,
                    emptyRowsWhenPaging: false,
                    rowStyle: (row: CatalogStockOrderTaskItem) => ({
                        backgroundColor: row.status === CatalogStockOrderTaskItemStatus.FAILURE ? '#fff5f5' : 'inherit',
                    }),
                }}
                onOrderChange={(colIdx, dir) => {
                    if (colIdx < 0) { setOrderBy(''); return; }
                    const col = columns[colIdx];
                    if (col && col.field) {
                        setOrderBy(dir === 'asc' ? col.field : `-${col.field}`);
                    }
                }}
                onChangePage={(p) => setPage(p)}
                totalCount={totalCount}
                page={page}
                localization={{ body: { emptyDataSourceMessage: loading ? 'Loading…' : EMPTY_ROW_MSG } }}
            />
        </div>
    );
};

interface Props {
    userData: any;
}

const NON_TERMINAL_STATUSES = [
    CatalogStockOrderTaskStatus.PENDING,
    CatalogStockOrderTaskStatus.STARTED,
    CatalogStockOrderTaskStatus.PROGRESS,
];

// Universal Catalog Ordering (FR-02): task history for the async processing
// triggered by catalog (MCPI) items in a stock order - matching/creating
// local Products and creating/updating the order on Korona (without booking).
const CatalogOrderTaskHistoryScreen: React.FC<Props> = ({ userData }) => {
    const classes = useStyles();
    const tableRef = useRef<any>(null);
    const latestTasksRef = useRef<CatalogStockOrderTask[]>([]);
    const pollingRef = useRef<any>(null);
    const [dataVersion, setDataVersion] = useState(0);

    const [searchFilter, setSearchFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [error, setError] = useState<string | null>(null);

    const businessId = userData && userData.selectedBusiness && userData.selectedBusiness.business_id;

    const fetchData = useCallback(
        (query: { page: number; pageSize: number; orderBy?: { field?: string }; orderDirection?: string; }) => {
            if (!businessId) {
                return Promise.resolve({ data: [], page: query.page, totalCount: 0 });
            }
            const orderByField = query.orderBy?.field || 'created_at';
            const orderDir = query.orderDirection || 'desc';
            const ordering = (orderDir === 'desc' ? '-' : '') + orderByField;
            return getCatalogStockOrderTasks(
                businessId,
                query.page + 1,
                query.pageSize,
                ordering,
                statusFilter || undefined,
                searchFilter || undefined
            )
                .then(checkStatus)
                .then(parseJSON)
                .then((data: any) => {
                    const results = data.results || data;
                    latestTasksRef.current = results;
                    setError(null);
                    setDataVersion((v) => v + 1);
                    return {
                        data: results,
                        page: query.page,
                        totalCount: data.count ?? results.length,
                    };
                })
                .catch((err: any) => {
                    handleError(err);
                    setError(ERROR_MSG);
                    throw err;
                });
        },
        [businessId, statusFilter, searchFilter]
    );

    // Auto-poll every 4s while any task is in a non-terminal state.
    useEffect(() => {
        const hasActiveTasks = latestTasksRef.current.some((t) =>
            NON_TERMINAL_STATUSES.includes(t.status as CatalogStockOrderTaskStatus)
        );
        if (hasActiveTasks && tableRef.current?.onQueryChange) {
            const q = tableRef.current.state?.query;
            if (q && !pollingRef.current) {
                pollingRef.current = setInterval(() => {
                    if (tableRef.current?.onQueryChange && tableRef.current.state?.query) {
                        tableRef.current.onQueryChange(tableRef.current.state.query);
                    }
                }, 4000);
            }
        } else if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, [dataVersion]);

    const isFirstMount = useRef(true);
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false;
            return;
        }
        if (tableRef.current?.onQueryChange && tableRef.current.state?.query) {
            tableRef.current.onQueryChange({ ...tableRef.current.state.query, page: 0 });
        }
    }, [statusFilter, searchFilter, businessId]);

    const columns = [
        {
            title: 'Stock Order #',
            field: 'stock_order_number',
            width: 160,
            render: (row: CatalogStockOrderTask) => (
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {row.stock_order_number || '—'}
                </Typography>
            ),
        },
        {
            title: 'Location',
            field: 'location_name',
            width: 160,
        },
        {
            title: 'Supplier',
            field: 'supplier_name',
            width: 180,
        },
        {
            title: 'Status',
            field: 'status',
            width: 140,
            render: (row: CatalogStockOrderTask) => <StatusChip status={row.status} />,
        },
        {
            title: 'Summary',
            field: 'items_count',
            sorting: false,
            render: (row: CatalogStockOrderTask) => (
                <div className={classes.summaryChips}>
                    <Tooltip title="Total items">
                        <Chip size="small" label={`${row.items_count} total`} style={{ backgroundColor: grey[200] }} />
                    </Tooltip>
                    {row.items_success_count > 0 && (
                        <Tooltip title="Successful">
                            <Chip size="small" label={`${row.items_success_count} ✓`} style={{ backgroundColor: green[100], color: green[800] }} />
                        </Tooltip>
                    )}
                    {row.items_failure_count > 0 && (
                        <Tooltip title="Failed">
                            <Chip size="small" label={`${row.items_failure_count} ✗`} style={{ backgroundColor: red[100], color: red[800] }} />
                        </Tooltip>
                    )}
                </div>
            ),
        },
        {
            title: 'Created',
            field: 'created_at',
            defaultSort: 'desc' as const,
            width: 160,
            render: (row: CatalogStockOrderTask) => moment(row.created_at).format('DD MMM YYYY HH:mm'),
        },
        {
            title: 'Last Updated',
            field: 'updated_at',
            width: 160,
            render: (row: CatalogStockOrderTask) => moment(row.updated_at).format('DD MMM YYYY HH:mm'),
        },
    ];

    return (
        <Container maxWidth={false} className={classes.root}>
            <Typography variant="h6" className={classes.pageTitle}>
                Stock Order Task History
            </Typography>

            <div className={classes.filterRow}>
                <TextField
                    label="Search Stock Order # / Task"
                    variant="outlined"
                    size="small"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: searchFilter ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchFilter('')}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : undefined,
                    }}
                    style={{ minWidth: 320 }}
                />
                <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                    <InputLabel id="catalog-task-status-filter-label">Filter by Status</InputLabel>
                    <Select
                        labelId="catalog-task-status-filter-label"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as string)}
                        label="Filter by Status"
                    >
                        <MenuItem value="">All</MenuItem>
                        {Object.values(CatalogStockOrderTaskStatus).map((s) => (
                            <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {error && <ErrorStatusBar message={error} />}

            <MaterialTable
                tableRef={tableRef}
                columns={columns}
                data={fetchData}
                options={{
                    toolbar: false,
                    paging: true,
                    pageSize: 20,
                    pageSizeOptions: [10, 20, 50],
                    search: false,
                    sorting: true,
                    draggable: false,
                    rowStyle: (row: CatalogStockOrderTask) => ({
                        backgroundColor:
                            row.status === CatalogStockOrderTaskStatus.FAILURE
                                ? '#fff5f5'
                                : row.status === CatalogStockOrderTaskStatus.SUCCESS
                                    ? '#f5fff5'
                                    : 'inherit',
                    }),
                }}
                localization={{ body: { emptyDataSourceMessage: EMPTY_ROW_MSG } }}
                detailPanel={[
                    {
                        tooltip: 'Show task items',
                        render: (row: CatalogStockOrderTask) => (
                            <Box padding={2} paddingTop={0}>
                                {row.progress_message && (
                                    <Box className={classes.progressMessageBox}>
                                        <Typography variant="overline" className={classes.progressMessageLabel} component="div">
                                            Progress
                                        </Typography>
                                        <Typography variant="body2" component="div">
                                            {row.progress_message}
                                        </Typography>
                                    </Box>
                                )}
                                <TaskItemsTable taskId={row.id} classes={classes} />
                            </Box>
                        ),
                    },
                ]}
            />
        </Container>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.userLocalData,
});

export default connect(mapStateToProps)(CatalogOrderTaskHistoryScreen);
